import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAPIRoute } from '@/utils/routing/apiRoutes';

export function buildApiEndpoint(endpoint: string): string {
  return '/api' + endpoint;
}

/**
 * Imperative POST to a Next.js `pages/api` route. Pass `route` like `usePost` does: a path
 * **relative to `/api`**, e.g. `'/auth/clear-mfa-session-satisfaction'`. Do not pass a
 * `route` that already includes `/api` (that would double-prefix to `/api/api/...`).
 * Use when React hooks cannot run (logout utilities, timers, etc.).
 */
export function postHubApi<T = unknown>(
  route: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  const fullEndpoint = buildApiEndpoint(getAPIRoute(route));
  return axios.post<T>(fullEndpoint, data ?? {}, config);
}

export function extractBody<T>(
  response: AxiosResponse<T> | undefined
): T | undefined {
  if (!response) return undefined;
  const data = response.data;
  if (data && typeof data === 'object' && 'body' in data) {
    return (data as { body: T }).body;
  }
  return data;
}

export function extractError(error: AxiosError | undefined): unknown {
  const errData = extractErrorData(error);
  if (errData && typeof errData === 'object' && 'body' in errData) {
    return (errData as { body: unknown }).body;
  }
  return error?.message;
}

export function extractErrorMessage(error: unknown): string | undefined {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return undefined;
}

export function extractErrorData(
  error: AxiosError | undefined
): Record<string, unknown> | undefined {
  return error?.response?.data as Record<string, unknown> | undefined;
}
